/**
 * Transform API responses from snake_case to camelCase
 * This ensures frontend components receive the correct field names
 */

import type { Celebrity, Media, LeaderboardItem, PaginatedResponse, Recommendation } from '@/types'

const DEFAULT_CELEBRITY_IMAGE = '/images/placeholders/celebrity.svg'
const DEFAULT_POSTER_IMAGE = '/images/placeholders/poster.svg'

/**
 * Transform celebrity data from snake_case to camelCase
 */
export function transformCelebrity(celebrity: any): Celebrity {
  return {
    id: celebrity.id,
    slug: celebrity.slug,
    name: celebrity.name,
    bio: celebrity.bio,
    category: celebrity.category,
    imageUrl: celebrity.image_url || DEFAULT_CELEBRITY_IMAGE,
    imageSourceUrl: celebrity.image_source_url || null,
    imageLicense: celebrity.image_license || null,
    imageAttribution: celebrity.image_attribution || null,
    imageProvider: celebrity.image_provider || null,
    birthYear: celebrity.birth_year,
    deathYear: celebrity.death_year,
    wikipediaUrl: celebrity.wikipedia_url,
    twitterHandle: celebrity.twitter_handle,
    websiteUrl: celebrity.website_url,
    isVerified: celebrity.is_verified,
    createdAt: celebrity.created_at,
  }
}

/**
 * Transform media data from snake_case to camelCase
 */
export function transformMedia(media: any): Media {
  return {
    id: media.id,
    slug: media.slug,
    title: media.title,
    originalTitle: media.original_title,
    type: media.type,
    year: media.year,
    endYear: media.end_year,
    description: media.description,
    posterUrl: media.poster_url || DEFAULT_POSTER_IMAGE,
    backdropUrl: media.backdrop_url || media.poster_url || DEFAULT_POSTER_IMAGE,
    trailerUrl: media.trailer_url,
    runtimeMinutes: media.runtime_minutes,
    tmdbId: media.tmdb_id,
    imdbId: media.imdb_id,
    rating: media.rating,
    imdbRating: media.imdb_rating ?? null,
    imdbVotes: media.imdb_votes ?? null,
    imdbRatingUpdatedAt: media.imdb_rating_updated_at ?? null,
    imdbRatingSource: media.imdb_rating_source ?? null,
    genres: media.genres || [],
    streamingLinks: parseWatchLinks(media.streaming_links),
    affiliateLinks: parseWatchLinks(media.affiliate_links),
  }
}

function parseWatchLinks(raw: unknown): import('@/types').WatchLink[] {
  if (!raw) return []
  if (Array.isArray(raw)) return raw
  if (typeof raw === 'object') {
    return Object.entries(raw as Record<string, string>).map(([platform, url]) => ({ platform, url }))
  }
  return []
}

/**
 * Transform leaderboard item from snake_case to camelCase
 */
export function transformLeaderboardItem(item: any): LeaderboardItem {
  return {
    id: item.id,
    slug: item.slug,
    title: item.title,
    type: item.type,
    year: item.year,
    posterUrl: item.poster_url || DEFAULT_POSTER_IMAGE,
    recommendationCount: item.recommendation_count,
    celebrityCount: item.celebrity_count,
  }
}

/**
 * Transform recommendation (including nested media/celebrity/source) from snake_case
 */
export function transformRecommendation(rec: any): Recommendation {
  return {
    id: rec.id,
    quote: rec.quote,
    context: rec.context,
    isFavorite: rec.is_favorite,
    confidenceScore: rec.confidence_score || 'paraphrase',
    createdAt: rec.created_at,
    celebrity: rec.celebrity ? transformCelebrity(rec.celebrity) : ({} as any),
    media: rec.media ? transformMedia(rec.media) : ({} as any),
    source: rec.source
      ? {
          id: rec.source.id,
          type: rec.source.type,
          title: rec.source.title,
          url: rec.source.url,
          publicationName: rec.source.publication_name,
          publishedDate: rec.source.published_date,
          description: rec.source.description,
          isVerified: rec.source.is_verified,
          waybackUrl: rec.source.wayback_url || null,
          evidenceCapturedAt: rec.source.evidence_captured_at || null,
          evidenceHash: rec.source.evidence_hash || null,
          evidenceExcerpt: rec.source.evidence_excerpt || null,
          evidenceScreenshotUrl: rec.source.evidence_screenshot_url || null,
        }
      : null,
  }
}

/**
 * Transform paginated response
 */
export function transformPaginatedResponse<T>(
  response: any,
  transformer: (item: any) => T
): PaginatedResponse<T> {
  return {
    data: (response.data || []).map(transformer),
    pagination: {
      page: response.pagination?.page || 1,
      perPage: response.pagination?.per_page || 20,
      total: response.pagination?.total || 0,
      totalPages: response.pagination?.total_pages || 1,
    },
  }
}

/**
 * Transform all API responses at once
 */
export function transformApiData<T>(data: any, transformer: (item: any) => T): T | T[] {
  if (Array.isArray(data)) {
    return data.map(transformer)
  }
  if (data.data && Array.isArray(data.data)) {
    return {
      ...data,
      data: data.data.map(transformer),
    } as any
  }
  return data as T
}